'use client'

import { useReducer, useEffect, useContext, useMemo } from 'react';
// import { useContextState } from './Context'; 
import { peopleReducer, peopleFilterReducer, inboxReducer } from './Reducers';
import { Profile } from '../models';

// --- TYPE DEFINITIONS ---
// Define the valid states as a constant array for easy checking
const VALID_SOCIAL_STATES = ['friends', 'requests', 'requestsSent', 'discovery'] as const;
export type SocialState = typeof VALID_SOCIAL_STATES[number]; // This creates the union type: 'friends' | 'requests' | ...

interface PeopleDataState {
    socialState: SocialState;
    friendsState: Profile[];
    requestsState: Profile[];
    requestsSentState: Profile[];
    friendsDiscoveryState: Profile[];
}

interface PeopleFilterState {
    searchQuery: string;
    sort: string;
    byRating: number;
    statusQuery: string;
}

// --- TYPE GUARD FUNCTION ---
// This function checks if a given string is a valid SocialState.
// The `value is SocialState` part is the type guard. If it returns true,
// TypeScript will know that `value` has the type `SocialState` inside the `if` block.
export function isValidSocialState(value: any): value is SocialState {
    return VALID_SOCIAL_STATES.includes(value);
}



// --- Default State Definitions ---
const defaultPeopleState: PeopleDataState = {
    socialState: 'friends', 
    friendsState: [],
    requestsState: [],
    requestsSentState: [],
    friendsDiscoveryState: [],
};

const defaultPeopleFilterState: PeopleFilterState = {
    searchQuery: "",
    sort: 'name_asc', 
    byRating: 0,
    statusQuery: "friends",
};

export const useSocialContext = (signedIn?: boolean) => {
    // const { accountCTX } = useContextState() || {};
    // const signedIn = accountCTX?.signedIn;

    const [peopleState, dispatchPeopleState] = useReducer(peopleReducer, defaultPeopleState);
    const [peopleFilterState, peopleFilterDispatch] = useReducer(peopleFilterReducer, defaultPeopleFilterState);
    

    // --- HYDRATION (Corrected) ---
    useEffect(() => {
        if (signedIn) {
            // 1. Get the stored string value
            const storedSocialState = window.localStorage.getItem("socialState");
            
            // 2. Use the type guard to validate it
            if (isValidSocialState(storedSocialState)) {
                // 3. Dispatch it. TypeScript now knows storedSocialState is a valid SocialState.
                dispatchPeopleState({ type: 'SET_SOCIAL_STATE', payload: storedSocialState });
            }
            
            // ... (rest of the hydration logic for filters is correct) ...
            const storedFiltersJSON = window.localStorage.getItem('peopleFilterState');
            if (storedFiltersJSON) {
                try {
                    const storedFilters = JSON.parse(storedFiltersJSON);
                    peopleFilterDispatch({ type: 'INITIALIZE_FILTERS', payload: storedFilters });
                } catch (error) {
                    console.error("Failed to parse stored people filters:", error);
                }
            }
        }
    }, [signedIn]);

    // ... (rest of the persistence and auto-reset effects are correct) ...
    useEffect(() => {
        if (signedIn && peopleState.socialState) {
            window.localStorage.setItem("socialState", peopleState.socialState);
        }
    }, [signedIn, peopleState.socialState]);
    
    useEffect(() => {
        if (signedIn) {
            window.localStorage.setItem('peopleFilterState', JSON.stringify(peopleFilterState));
        }
    }, [signedIn, peopleFilterState]);

    useEffect(() => {
        if (signedIn === false) {
            dispatchPeopleState({ type: 'RESET_STATE' });
            peopleFilterDispatch({ type: 'RESET_FILTERS' });
            window.localStorage.removeItem("socialState");
            window.localStorage.removeItem("peopleFilterState");
        }
    }, [signedIn]);

    const contextValue = useMemo(() => ({
        peopleState, 
        dispatchPeopleState, 
        peopleFilterState, 
        peopleFilterDispatch 
    }), [
        peopleState, 
        dispatchPeopleState, 
        peopleFilterState, 
        peopleFilterDispatch 
    ]);

    return contextValue;


    // return { peopleState, dispatchPeopleState, peopleFilterState, peopleFilterDispatch };
}

export type SocialContextValue = ReturnType<typeof useSocialContext>;